import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, MapPin, Search, Map } from 'lucide-react';
import { Stop } from '../../types';
import { stopApi } from '../../services/api';
import LoadingSpinner from '../ui/LoadingSpinner';

const RouteManagement: React.FC = () => {
  const [stops, setStops] = useState<Stop[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingStop, setEditingStop] = useState<Stop | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');

  useEffect(() => {
    fetchStops();
  }, []);

  const fetchStops = async () => {
    try {
      setLoading(true);
      const response = await stopApi.getAll();
      if (response.success && response.data) {
        setStops(response.data);
      }
    } catch (err) {
      setError('Failed to fetch stops');
    } finally {
      setLoading(false);
    }
  };

  // const handleAddStop = async (stopData: Partial<Stop>) => {
  //   try {
  //     const response = await stopApi.create(stopData);
  //     if (response.success && response.data) {
  //       setStops([...stops, response.data]);
  //       setShowAddModal(false);
  //     }
  //   } catch (err) {
  //     setError('Failed to add stop');
  //   }
  // };

  // const handleEditStop = async (stopData: Partial<Stop>) => {
  //   if (!editingStop) return;
  //   
  //   try {
  //     const response = await stopApi.update(editingStop.id, stopData);
  //     if (response.success && response.data) {
  //       setStops(stops.map(s => s.id === editingStop.id ? response.data! : s));
  //       setEditingStop(null);
  //     }
  //   } catch (err) {
  //     setError('Failed to update stop');
  //   }
  // };

  const handleDeleteStop = async (id: number) => {
    if (!confirm('Are you sure you want to delete this stop?')) return;
    
    try {
      const response = await stopApi.delete(id);
      if (response.success) {
        setStops(stops.filter(s => s.id !== id));
      }
    } catch (err) {
      setError('Failed to delete stop');
    }
  };

  const filteredStops = stops.filter(stop => {
    const matchesSearch = stop.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         stop.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || stop.type === filterType;
    return matchesSearch && matchesFilter;
  });

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'pickup': return 'bg-blue-100 text-blue-800';
      case 'delivery': return 'bg-green-100 text-green-800';
      case 'depot': return 'bg-purple-100 text-purple-800';
      case 'checkpoint': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-fleet-dark">Route & Stop Management</h1>
          <p className="text-fleet-neutral">Manage delivery stops and geofenced areas</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Stop
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-fleet p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-fleet-neutral h-5 w-5" />
              <input
                type="text"
                placeholder="Search stops..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <Map className="h-5 w-5 text-fleet-neutral" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="pickup">Pickup</option>
              <option value="delivery">Delivery</option>
              <option value="depot">Depot</option>
              <option value="checkpoint">Checkpoint</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800 ml-4"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Stops Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredStops.map((stop) => (
          <div key={stop.id} className="bg-white rounded-lg shadow-fleet p-6 card-hover">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="h-12 w-12 bg-fleet-primary bg-opacity-10 rounded-full flex items-center justify-center">
                  <MapPin className="h-6 w-6 text-fleet-primary" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-fleet-dark">{stop.name}</h3>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(stop.type)}`}>
                    {stop.type}
                  </span>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setEditingStop(stop)}
                  className="text-fleet-primary hover:text-fleet-secondary"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteStop(stop.id)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-sm text-fleet-neutral">
                <span className="font-medium">Address:</span>
                <p className="mt-1">{stop.address}</p>
              </div>
              <div className="flex items-center text-sm text-fleet-neutral">
                <span className="font-medium">Coordinates:</span>
                <span className="ml-2">{Number(stop.latitude).toFixed(6)}, {Number(stop.longitude).toFixed(6)}</span>
              </div>
              <div className="flex items-center text-sm text-fleet-neutral">
                <span className="font-medium">Radius:</span>
                <span className="ml-2">{stop.radius} meters</span>
              </div>
            </div>

            {/* Detection Status - placeholder */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm text-fleet-neutral">Detection Status</span>
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                  Active
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Stop Form Modal - simplified for demo */}
      {(showAddModal || editingStop) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-medium text-fleet-dark mb-4">
                {editingStop ? 'Edit Stop' : 'Add New Stop'}
              </h3>
              
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Stop Name"
                  defaultValue={editingStop?.name || ''}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
                />
                <textarea
                  placeholder="Address"
                  defaultValue={editingStop?.address || ''}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
                />
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="number"
                    placeholder="Latitude"
                    defaultValue={editingStop?.latitude || ''}
                    step="any"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
                  />
                  <input
                    type="number"
                    placeholder="Longitude"
                    defaultValue={editingStop?.longitude || ''}
                    step="any"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <select 
                    defaultValue={editingStop?.type || 'pickup'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
                  >
                    <option value="pickup">Pickup</option>
                    <option value="delivery">Delivery</option>
                    <option value="depot">Depot</option>
                    <option value="checkpoint">Checkpoint</option>
                  </select>
                  <input
                    type="number"
                    placeholder="Radius (meters)"
                    defaultValue={editingStop?.radius || 100}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingStop(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Handle form submission here
                    setShowAddModal(false);
                    setEditingStop(null);
                  }}
                  className="px-4 py-2 bg-fleet-primary text-white rounded-lg hover:bg-fleet-secondary"
                >
                  {editingStop ? 'Update' : 'Add'} Stop
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteManagement;